import { useState } from 'react';
import { Text } from '@chakra-ui/react';

import placeholderStudentVisaExample from '../../../../assets/images/sample-student-visa.jpg';
import placeholderOtherVisaExample from '../../../../assets/images/sample-other-visas.jpg';

import { isValidDateAndPast, isValidDate } from '../../../../utilities/validation';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import { ModalColumns } from '../../../atoms/modal';

import AcknowledgeBox from '../../../molecules/acknowledgeBox';
import UploadActions from '../../../atoms/upload-actions';
import UploadCompleteSection from '../../../molecules/uploadComplete';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import UploadExample from '../../../atoms/upload-example';

export interface IProps {
  complete: boolean;
  fileNames: string[];
  isStudentVisa: boolean;
  status: string;
  onClose: () => void;
  onContinue: () => void;
  onUpload: (metadata?: any) => void;
  startDate?: string;
  endDate?: string;
  visaType?: string;
  year?: string;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

export const PreviousUKVisaModal: React.FC<IProps> = (props: IProps) => {
  const { docKey, infoOnly, docStatus, isStudentVisa } = props;

  const [startDate, setStartDate] = useState(getDateFromString(props.startDate));
  const [endDate, setEndDate] = useState(getDateFromString(props.endDate));

  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');

  const questionsComplete =
    startDate.DD !== '' &&
    startDate.DD !== null &&
    startDate.MM !== '' &&
    startDate.MM !== null &&
    startDate.YYYY !== '' &&
    startDate.YYYY !== null &&
    startDateError === '' &&
    endDate.DD !== '' &&
    endDate.DD !== null &&
    endDate.MM !== '' &&
    endDate.MM !== null &&
    endDate.YYYY !== '' &&
    endDate.YYYY !== null &&
    endDateError === '';

  const handleUpload = () => {
    props.onUpload({
      startDate: getStringFromDate(startDate),
      endDate: getStringFromDate(endDate)
    });
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (_key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  return (
    <ModalColumns
      id={'previousUKVisa'}
      title={`${isStudentVisa ? 'Previous Student Visa' : 'Other Visa'}`}
      onClose={handleOnClose}
      size="4xl"
      supportingColumn={
        <UploadExample
          altText="Visa example"
          url={isStudentVisa ? placeholderStudentVisaExample : placeholderOtherVisaExample}
        />
      }
    >
      {infoOnly ? (
        <>
          <Text>
            {isStudentVisa
              ? `You have indicated that you have a prior UK student visa. This document may be required during your visa application.`
              : `You have indicated that you have a prior UK visa. Please scan this document and upload it here.`}
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={handleUpload} onContinue={props.onContinue} />
          ) : (
            <>
              <Text>
                {isStudentVisa
                  ? `You have indicated that you have a prior UK student visa. This document may be required during your visa application.`
                  : `You have indicated that you have a prior UK visa. Please scan this document and upload it here.`}
              </Text>

              <DateInputWithConfirm
                id={'visa-start-date'}
                label={'Enter the start date for this visa (as stated on the visa itself)'}
                errorText={startDateError}
                value={startDate}
                onChangeDD={(evt: any) => {
                  setStartDate({
                    ...startDate,
                    DD: evt.currentTarget.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setStartDate({
                    ...startDate,
                    MM: evt.currentTarget.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setStartDate({
                    ...startDate,
                    YYYY: evt.currentTarget.value
                  });
                }}
                onBlur={() => {
                  if (!isValidDateAndPast(startDate)) {
                    setStartDateError(`Date must be valid`);
                  }
                }}
                onFocus={() => {
                  if (startDateError !== '') {
                    setStartDateError('');
                  }
                }}
              />

              <DateInputWithConfirm
                id={'visa-end-date'}
                label={'Enter the end date for this visa (as stated on the visa itself)'}
                errorText={endDateError}
                value={endDate}
                onChangeDD={(evt: any) => {
                  setEndDate({
                    ...endDate,
                    DD: evt.currentTarget.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setEndDate({
                    ...endDate,
                    MM: evt.currentTarget.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setEndDate({
                    ...endDate,
                    YYYY: evt.currentTarget.value
                  });
                }}
                onBlur={() => {
                  if (!isValidDate(endDate)) {
                    setEndDateError(`Date must be valid`);
                  }
                }}
                onFocus={() => {
                  if (endDateError !== '') {
                    setEndDateError('');
                  }
                }}
              />

              <UploadActions complete={questionsComplete} label={'Save & Continue'} onContinue={handleUpload} />
            </>
          )}
        </>
      )}
    </ModalColumns>
  );
};
